import React, { useState } from "react";
import { Button } from "../../../../../../ui/components/Button";
import { FeatherDownload, FeatherArrowLeft } from "@subframe/core";
import { PivotTableData, PivotConfig, PivotView } from "../types";
import { PivotPreviewTable } from "./PivotPreviewTable";
import { exportPivotToExcel, exportAllPivotTabsToExcel } from "../../../../data/api/pivotApi";
import { SaveAllTabsModal } from "./SaveAllTabsModal";

interface Step3ResultsProps {
  pivotConfig: PivotConfig;
  pivotData: PivotTableData | null;
  viewName?: string;
  viewId?: string; // If exporting a saved view
  fileId?: string; // If exporting an unsaved view
  allTabs?: PivotView[]; // All tabs for multi-tab export
  allTabsData?: Record<string, PivotTableData>; // Pivot data for all tabs (keyed by view ID)
  onBack: () => void;
  onExport?: () => void;
  onEditView?: () => void;
  onCreateNewView?: () => void;
  onSaveAllTabs?: (tabNames: Record<string, string>, registerSetName?: string) => Promise<{ savedCount: number; savedViewIds: string[]; failureCount: number }>; // Callback to save all tabs with names
  autoSaveAllTabs?: () => Promise<{ savedCount: number; savedViewIds: string[]; failureCount: number }>; // Auto-save callback for export (uses auto-generated names)
  registerSetName?: string; // Current register set name
  onUpdateRegisterSetName?: (setName: string) => Promise<void>; // Callback to update register set name
  isSaving?: boolean; // Loading state from parent
}

export const Step3Results: React.FC<Step3ResultsProps> = ({
  pivotConfig,
  pivotData,
  viewName = 'Pivot View',
  viewId,
  fileId,
  allTabs = [],
  allTabsData = {},
  onBack,
  onExport,
  onEditView,
  onCreateNewView,
  onSaveAllTabs,
  autoSaveAllTabs,
  registerSetName,
  onUpdateRegisterSetName,
  isSaving = false,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showSaveAllModal, setShowSaveAllModal] = useState(false);
  const [pendingExport, setPendingExport] = useState(false);

  // Check if we should export all tabs
  const hasMultipleTabs = allTabs.length > 1;
  const savedTabs = allTabs.filter(tab => tab.savedViewId);
  const unsavedTabs = allTabs.filter(tab => !tab.savedViewId || !tab.isSaved);
  const unsavedTabsCount = unsavedTabs.length;

  // For "Use with New Data" flow, always export all tabs if multiple exist
  const shouldExportAll = hasMultipleTabs && fileId;

  const handleExport = async () => {
    // If multiple tabs exist and there are unsaved tabs, show save modal first
    if (shouldExportAll && unsavedTabsCount > 0) {
      setPendingExport(true);
      setShowSaveAllModal(true);
      return;
    }
    
    // If multiple tabs exist and all are saved, export directly
    if (shouldExportAll) {
      await handleExportAllTabs();
      return;
    }

    // Otherwise, export single tab (current behavior)
    if (!viewId && !fileId) {
      alert('Cannot export: No file ID available. Please generate the pivot table first.');
      return;
    }

    setIsExporting(true);
    try {
      const exportRequest: any = {
        viewName: viewName,
        options: {
          includeConfigSheet: true,
        },
      };

      if (viewId) {
        // Export saved view - backend will load it
        exportRequest.viewId = viewId;
      } else {
        // Export unsaved view - send fileId + config
        exportRequest.fileId = fileId;
        exportRequest.config = {
          rowGroups: pivotConfig.rowGroups.map(f => ({
            name: f.name,
            id: f.id,
            columnId: f.columnId
          })),
          columnLabels: pivotConfig.columnLabels.map(f => ({
            name: f.name,
            id: f.id,
            columnId: f.columnId
          })),
          values: pivotConfig.values.map(f => ({
            name: f.name,
            aggregation: f.aggregation || 'sum',
            id: f.id,
            columnId: f.columnId
          })),
          globalFilters: pivotConfig.globalFilters.map(f => ({
            name: f.name,
            id: f.id,
            columnId: f.columnId
          })),
        };
      }

      await exportPivotToExcel(exportRequest);

      // Call optional callback
      onExport?.();
    } catch (error: any) {
      console.error('Failed to export to Excel:', error);
      alert(`Failed to export: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllTabs = async () => {
    if (!fileId) {
      alert('Cannot export: No file ID available.');
      return;
    }

    setIsExporting(true);
    try {
      // Get view IDs from allTabs - all tabs should already be saved at this point
      // (unsaved tabs trigger modal first via handleExport)
      const allSavedTabs = allTabs.filter(tab => tab.savedViewId);
      const viewIds = allSavedTabs
        .map(tab => tab.savedViewId)
        .filter((id): id is string => !!id);

      if (viewIds.length === 0) {
        alert('No tabs to export. Please ensure tabs are configured and saved.');
        return;
      }

      console.log(`Exporting ${viewIds.length} pivot view(s) with IDs:`, viewIds);
      console.log('All tabs state:', allTabs.map(tab => ({ id: tab.id, name: tab.name, savedViewId: tab.savedViewId, isSaved: tab.isSaved })));

      await exportAllPivotTabsToExcel({
        fileId: fileId,
        viewIds: viewIds,
        viewName: 'All Pivot Views',
        options: {
          includeConfigSheet: true,
        },
      });

      alert(`Successfully exported ${viewIds.length} pivot view${viewIds.length !== 1 ? 's' : ''}!`);
      onExport?.();
    } catch (error: any) {
      console.error('Failed to export all tabs:', error);
      alert(`Failed to export: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveAllTabs = () => {
    setPendingExport(false); // Not exporting, just saving
    setShowSaveAllModal(true);
  };

  const handleConfirmSaveAll = async (
    tabNames: Record<string, string>,
    registerSetName?: string
  ) => {
    if (onSaveAllTabs) {
      try {
        const result = await onSaveAllTabs(tabNames, registerSetName);
        setShowSaveAllModal(false);
        
        // If export was pending, directly export using the saved view IDs
        if (pendingExport && result && result.savedViewIds && result.savedViewIds.length > 0) {
          setPendingExport(false);
          // Export directly using the IDs we just saved - no need to call autoSaveAllTabs again
          await exportAfterSave(result.savedViewIds);
        } else if (pendingExport) {
          setPendingExport(false);
        }
      } catch (error) {
        // Error handling is done in parent
        console.error('Failed to save all tabs:', error);
        setPendingExport(false);
      }
    }
  };

  // Export using saved view IDs directly (skips auto-save to avoid race condition)
  const exportAfterSave = async (viewIds: string[]) => {
    if (!fileId || viewIds.length === 0) {
      alert('Cannot export: No saved tabs available.');
      return;
    }

    setIsExporting(true);
    try {
      console.log(`Exporting ${viewIds.length} pivot view(s) with IDs:`, viewIds);

      await exportAllPivotTabsToExcel({
        fileId: fileId,
        viewIds: viewIds,
        viewName: 'All Pivot Views',
        options: {
          includeConfigSheet: true,
        },
      });

      alert(`Successfully exported ${viewIds.length} pivot view${viewIds.length !== 1 ? 's' : ''}!`);
      onExport?.();
    } catch (error: any) {
      console.error('Failed to export all tabs:', error);
      alert(`Failed to export: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6 h-full">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-heading-2 font-heading-2 text-default-font mb-2">
            Pivot Table Results
          </h2>
          <p className="text-body font-body text-subtext-color">
            Review your pivot table and export if needed
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onSaveAllTabs && allTabs.length > 0 && (
            <Button
              variant="neutral-secondary"
              onClick={handleSaveAllTabs}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save All Tabs'}
            </Button>
          )}
          <Button
            variant="brand-primary"
            icon={<FeatherDownload />}
            onClick={handleExport}
            disabled={isExporting || !pivotData}
          >
            {isExporting
              ? 'Exporting...'
              : hasMultipleTabs
                ? 'Export All Tabs'
                : 'Export to Excel'}
          </Button>
        </div>
      </div>


      {/* Configuration Summaries + Previews - Show all tabs */}
      {hasMultipleTabs ? (
        <div className="space-y-6">
          {allTabs.map((tab, index) => (
            <div key={tab.id} className="rounded-md border border-neutral-border overflow-hidden">
              {/* Configuration Summary Header */}
              <div className="p-4 bg-neutral-50 border-b border-neutral-border">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-body-bold font-body-bold text-default-font">
                    {tab.name}
                  </h3>
                  {tab.id === viewId && (
                    <span className="text-xs px-2 py-1 bg-brand-100 text-brand-700 rounded">Current View</span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-caption font-caption text-subtext-color">Row Groups</span>
                    <p className="text-body font-body text-default-font">
                      {tab.config.rowGroups.length > 0
                        ? tab.config.rowGroups.map((f) => f.name).join(", ")
                        : "None"}
                    </p>
                  </div>
                  <div>
                    <span className="text-caption font-caption text-subtext-color">Column Labels</span>
                    <p className="text-body font-body text-default-font">
                      {tab.config.columnLabels.length > 0
                        ? tab.config.columnLabels.map((f) => f.name).join(", ")
                        : "None"}
                    </p>
                  </div>
                  <div>
                    <span className="text-caption font-caption text-subtext-color">Values</span>
                    <p className="text-body font-body text-default-font">
                      {tab.config.values.length > 0
                        ? tab.config.values.map((f) => `${f.name} (${f.aggregation || "sum"})`).join(", ")
                        : "None"}
                    </p>
                  </div>
                  <div>
                    <span className="text-caption font-caption text-subtext-color">Filters</span>
                    <p className="text-body font-body text-default-font">
                      {tab.config.globalFilters.length > 0
                        ? tab.config.globalFilters.map((f) => f.name).join(", ")
                        : "None"}
                    </p>
                  </div>
                </div>
              </div>
              {/* Preview Table */}
              {allTabsData[tab.id] && (
                <div className="max-h-[300px] overflow-auto">
                  <PivotPreviewTable config={tab.config} data={allTabsData[tab.id]} />
                </div>
              )}
              {!allTabsData[tab.id] && (
                <div className="p-4 text-center text-subtext-color">
                  No preview available - generate pivot table first
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-neutral-border overflow-hidden">
          {/* Configuration Summary Header */}
          <div className="p-4 bg-neutral-50 border-b border-neutral-border">
            <h3 className="text-body-bold font-body-bold text-default-font mb-3">
              Configuration Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="text-caption font-caption text-subtext-color">Row Groups</span>
                <p className="text-body font-body text-default-font">
                  {pivotConfig.rowGroups.length > 0
                    ? pivotConfig.rowGroups.map((f) => f.name).join(", ")
                    : "None"}
                </p>
              </div>
              <div>
                <span className="text-caption font-caption text-subtext-color">Column Labels</span>
                <p className="text-body font-body text-default-font">
                  {pivotConfig.columnLabels.length > 0
                    ? pivotConfig.columnLabels.map((f) => f.name).join(", ")
                    : "None"}
                </p>
              </div>
              <div>
                <span className="text-caption font-caption text-subtext-color">Values</span>
                <p className="text-body font-body text-default-font">
                  {pivotConfig.values.length > 0
                    ? pivotConfig.values.map((f) => `${f.name} (${f.aggregation || "sum"})`).join(", ")
                    : "None"}
                </p>
              </div>
              <div>
                <span className="text-caption font-caption text-subtext-color">Filters</span>
                <p className="text-body font-body text-default-font">
                  {pivotConfig.globalFilters.length > 0
                    ? pivotConfig.globalFilters.map((f) => f.name).join(", ")
                    : "None"}
                </p>
              </div>
            </div>
          </div>
          {/* Preview Table */}
          {pivotData && (
            <div className="max-h-[400px] overflow-auto">
              <PivotPreviewTable config={pivotConfig} data={pivotData} />
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-neutral-border">
        <Button
          variant="neutral-tertiary"
          icon={<FeatherArrowLeft />}
          onClick={onBack}
          className="hidden"
        >
          Back to Builder
        </Button>
      </div>

      {/* Save All Tabs Modal - shows all tabs for naming/saving */}
      {allTabs.length > 0 && (
        <SaveAllTabsModal
          isOpen={showSaveAllModal}
          onClose={() => {
            setShowSaveAllModal(false);
            setPendingExport(false); // Cancel pending export if modal is closed
          }}
          onConfirm={handleConfirmSaveAll}
          tabs={allTabs}
          isSaving={isSaving}
          registerName={registerSetName}
          fileId={fileId}
          onSetName={onUpdateRegisterSetName ? async (setName: string) => {
            if (fileId && onUpdateRegisterSetName) {
              await onUpdateRegisterSetName(setName);
            }
          } : undefined}
        />
      )}
    </div>
  );
};

